<template>
  <form name="form" v-on:submit.prevent="submit" v-on:change="changeHandler">
    <fieldset
      :disabled="readonly"
      style="border:0;padding:0;margin:0;min-width:0;"
    >
      <div class="card-body">
        <div class="alert alert-info text-center" v-if="!source?.referrer">
          <p class="m-0">
            You must set the referrer before you can move on
          </p>
        </div>

        <div class="float-right" v-if="!readonly">
          <form-button :indicator="form.processing()" v-on:clicked="submit">
            Save
          </form-button>
        </div>
        <div class="clearfix"></div>
        <hr />

        <div class="col-xl-12">
          <div class="row">
            <div class="col-lg-6">
              <inputbox
                v-model="form.claim_reference"
                name="claim_reference"
                label="Claim Reference"
                :error-message="form.errors.get('claim_reference')"
                helper="Please enter the claim reference"
                :show-error="form.errors.has('claim_reference')"
                :readonly="true"
              ></inputbox>

              <inputbox
                v-model="form.source_reference"
                name="source_reference"
                label="Source Reference"
                :error-message="form.errors.get('source_reference')"
                helper="Please enter the source reference"
                :show-error="form.errors.has('source_reference')"
                :readonly="readonly"
              ></inputbox>

              <inputbox
                :value="author?.full_name"
                name="author"
                label="Created By"
                helper="The user that created the claim"
                :readonly="true"
              ></inputbox>

              <div class="form-group">
                <div class="col-md-4">
                  <label class="control-label">Handler:</label>
                  <p class="help-block">
                    The handler the claim has been assigned to
                  </p>
                </div>
                <div class="col-md-8">
                  <div
                    :class="{
                      'input-group':
                        !readonly && hasPermission('claims.reassign')
                    }"
                  >
                    <input
                      type="text"
                      name="handler"
                      readonly="readonly"
                      class="form-control"
                      :value="handler?.full_name || 'Not Assigned'"
                    />
                    <span
                      class="input-group-btn"
                      v-if="!readonly && hasPermission('claims.reassign')"
                    >
                      <button
                        type="button"
                        class="btn btn-info"
                        @click="openAssignHandlerModal"
                      >
                        Assign Handler
                      </button>
                    </span>
                  </div>
                  <div class="clearfix"></div>
                </div>
              </div>

              <div
                class="form-group has-helper"
                :class="{
                  'has-error': form.errors.has('incident_occurred_at')
                }"
              >
                <div class="col-md-4">
                  <label class="control-label">Incident Date/Time:</label>
                  <p class="help-block">
                    Please enter the date and time of the incident
                  </p>
                </div>
                <div class="col-md-8">
                  <datetimepicker
                    :value="form.incident_occurred_at"
                    name="incident_occurred_at"
                    :disabled="disabledDates.incident"
                    v-on:selected="setIncidentDate"
                    v-on:input="changeHandler"
                    input-class="form-control"
                    :readonly="readonly"
                  ></datetimepicker>
                  <span
                    class="text-danger"
                    v-text="form.errors.get('incident_occurred_at')"
                    v-if="form.errors.has('incident_occurred_at')"
                  ></span>
                </div>
              </div>
              <div
                class="form-group has-helper"
                :class="{ 'has-error': form.errors.has('referred_at') }"
              >
                <div class="col-md-4">
                  <label class="control-label">Date Referred:</label>
                  <p class="help-block">
                    Please enter the date the incident was referred
                  </p>
                </div>
                <div class="col-lg-8">
                  <datepicker
                    format="dd/MM/yyyy"
                    :value="form.referred_at"
                    name="referred_at"
                    v-on:selected="setReferredDate"
                    v-on:input="changeHandler"
                    input-class="form-control"
                    placeholder="dd/mm/yyyy"
                    :disabled="disabledDates.referred"
                    :readonly="readonly"
                    :required="true"
                  ></datepicker>
                  <span
                    class="text-danger"
                    v-text="form.errors.get('referred_at')"
                    v-if="form.errors.has('referred_at')"
                  ></span>
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <!--            <div class="form-group">-->
              <!--              <div class="col-md-4">-->
              <!--                <label class="control-label">Referrer Type:</label>-->
              <!--                <p class="help-block">-->
              <!--                  Please choose the referrer type for this incident-->
              <!--                </p>-->
              <!--              </div>-->
              <!--              <div class="col-md-8">-->
              <!--                <form-navigation-tabs-->
              <!--                    :tabs="tabs"-->
              <!--                    v-on:selected="setActive"-->
              <!--                    :active="referrer_type"-->
              <!--                ></form-navigation-tabs>-->
              <!--              </div>-->
              <!--            </div>-->

              <selectlist
                :options="referrers"
                v-model="form.referrer_id"
                :show-error="form.errors.has('referrer_id')"
                :error-message="form.errors.get('referrer_id')"
                label="Referrer"
                label-name="name"
                helper="Please select a referrer from the list"
                placeholder="Please Select"
                name="referrers"
                :readonly="readonly"
              ></selectlist>

              <selectlist
                v-model="form.incident_claim_type_id"
                :options="types"
                :show-error="form.errors.has('incident_claim_type_id')"
                :error-message="form.errors.get('incident_claim_type_id')"
                label="Claim Type"
                name="incident_claim_type_id"
                placeholder="Please Select"
                helper="Please select the incident claim type"
                :readonly="readonly"
              ></selectlist>

              <selectlist
                v-model="form.theft_status_id"
                :options="theftStatuses"
                :show-error="form.errors.has('theft_status_id')"
                label="Theft Status"
                name="theft_status_id"
                placeholder="Please Select"
                helper="Please select the theft status"
                :readonly="readonly"
                v-if="claimType?.name === 'theft'"
              ></selectlist>

              <selectlist
                v-model="form.claim_route_id"
                :options="claimRoutes"
                :show-error="form.errors.has('claim_route_id')"
                label="Claim Route"
                name="claim_route_id"
                key-name="id"
                placeholder="Please Select"
                helper="Please select the incident claim type"
                :readonly="readonly"
              ></selectlist>

              <inputbox
                v-model="form.individual"
                name="individual"
                helper="Please enter the name of the contact"
                label="Contact"
                :readonly="readonly"
              ></inputbox>

              <company-select
                v-if="companyLoaded"
                id="reference-company"
                :company="solicitorForm"
                ref="companySelector"
                heading="Solicitor"
                helper="Enter the details of solicitor"
                :types="solicitorTypes"
                :readonly="readonly"
              ></company-select>

              <inputbox
                v-model="form.solicitors_reference"
                name="solicitors_reference"
                helper="Please enter the solicitors reference"
                label="Solicitors Reference"
                :readonly="readonly"
              ></inputbox>

              <!--            <div class="form-group">-->
              <!--              <div class="col-sm-4">-->
              <!--                <label class="control-label">-->
              <!--                  Is The Client Vulnerable?-->
              <!--                </label>-->
              <!--              </div>-->
              <!--              <div class="col-sm-8">-->
              <!--                <toggleswitch-->
              <!--                    :labels="true"-->
              <!--                    name="vulnerable"-->
              <!--                    v-model="form.vulnerable"-->
              <!--                ></toggleswitch>-->
              <!--              </div>-->
              <!--            </div>-->

              <!--            <div v-if="form.vulnerable">-->
              <!--              <selectlist-->
              <!--                  v-model="form.vulnerability_code_id"-->
              <!--                  :options="vulnerabilityCodes"-->
              <!--                  :show-error="form.errors.has('vulnerability_code_id')"-->
              <!--                  label="Vulnerable Code"-->
              <!--                  name="vulnerability_code_id"-->
              <!--                  key-name="id"-->
              <!--                  placeholder="Please Select"-->
              <!--                  helper="Please select the vulnerable code"-->
              <!--              ></selectlist>-->

              <!--              <input-block-->
              <!--                  v-model="form.information_provided"-->
              <!--                  name="information_provided"-->
              <!--                  label="Information Provided"-->
              <!--                  helper="Please enter the information client has provided about their vulnerability"-->
              <!--              ></input-block>-->

              <!--              <input-block-->
              <!--                  v-model="form.support_required"-->
              <!--                  name="support_required"-->
              <!--                  label="Support Required"-->
              <!--                  helper="Please enter the support the client requires"-->
              <!--              ></input-block>-->

              <!--              <input-block-->
              <!--                  v-model="form.support_provided"-->
              <!--                  name="support_provided"-->
              <!--                  label="Support Provided"-->
              <!--                  helper="Please enter the details of the support we have provided for the client"-->
              <!--              ></input-block>-->
              <!--            </div>-->

              <!--<div v-if="referrer_type == 'individual'">-->
              <!--<inputbox v-model="form.referrer_first_name" name="referrer_first_name"-->
              <!--helper="Please enter the first name of the referrer" label="First Name"></inputbox>-->
              <!--<inputbox v-model="form.referrer_last_name" name="referrer_last_name"-->
              <!--helper="Please enter the last name of the referrer" label="Last Name"></inputbox>-->
              <!--<inputbox v-model="form.referrer_telephone" name="referrer_telephone"-->
              <!--helper="Please enter the telephone number of the referrer" label="Telephone"></inputbox>-->
              <!--<inputbox v-model="form.referrer_email_address" name="referrer_email_address" type="email"-->
              <!--helper="Please enter the email address of the referrer" label="Email Address"></inputbox>-->
              <!--</div>-->
            </div>
          </div>

          <vulnerabilities
            :incident="incident"
            :readonly="readonly"
          ></vulnerabilities>
        </div>
      </div>
      <div class="card-footer" v-if="!readonly">
        <div class="float-left">
          <div class="btn btn-danger" v-if="showCancel" v-on:click="cancel">
            Cancel
          </div>
        </div>

        <div class="float-right">
          <form-button :indicator="form.processing()" v-on:clicked="submit">
            Save
          </form-button>
        </div>

        <div class="clearfix"></div>
      </div>
    </fieldset>

    <assign-handler
      :incident="incident"
      v-if="incident"
      :handlers="handlers"
      :show="modals.assign"
      v-on:close="closeAssignToHandlerModal"
      v-on:assigned="handlerWasAssigned"
    ></assign-handler>
  </form>
</template>
<script>
import AssignHandler from '@/components/Modules/Shared/AssignHandler';
import Vulnerabilities from '@/components/Modules/FirstNoticeOfLoss/Sections/Vulnerabilities.vue';
import Company from '@/components/Modules/Shared/Company.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },
    showCancel: {
      type: Boolean,
      required: true
    },
    readonly: {
      type: Boolean,
      required: false
    }
  },

  components: {
    'company-select': Company,
    Vulnerabilities,
    AssignHandler
  },

  data() {
    return {
      form: new window.Form({
        claim_reference: null,
        source_reference: null,
        incident_occurred_at: null,
        referred_at: null,
        referrer_id: null,
        referrer_first_name: null,
        referrer_last_name: null,
        referrer_telephone: null,
        referrer_email_address: null,
        individual: null,
        incident_claim_type_id: null,
        claim_route_id: null,
        theft_status_id: null,
        vulnerable: null,
        vulnerability_code_id: null,
        information_provided: null,
        support_required: null,
        support_provided: null,
        solicitor_id: null,
        solicitor_name: null,
        solicitor_email_address: null,
        solicitor_telephone_number: null,
        solicitor_address_line_one: null,
        solicitor_address_line_two: null,
        solicitor_city: null,
        solicitor_postcode: null,
        solicitors_reference: null
      }),
      solicitorForm: new window.Form({
        company_id: null,
        name: null,
        email_address: null,
        telephone_number: null,
        address_line_one: null,
        address_line_two: null,
        city: null,
        postcode: null
      }),
      source_exists: false,
      modals: {
        assign: false
      },
      data_entered: false,
      companyLoaded: false,
      solicitorTypes: ['solicitor']
    };
  },

  mounted() {
    this.mount();
  },

  computed: {
    showReferrer() {
      return this.referrer_type === 'company';
    },

    disabledDates() {
      return {
        referred: {
          to: this.referredFromDate,
          from: new Date()
        },
        incident: {
          from: new Date()
        }
      };
    },

    incidentDateTime() {
      if (this.source_exists && this.form) {
        return window.DateManager.parse(this.form.incident_occurred_at);
      }

      return this.form ? this.form.incident_occurred_at : null;
    },

    referredFromDate() {
      if (!this.form.incident_occurred_at) {
        return new Date();
      }
      let date = window.DateManager.parse(this.form.incident_occurred_at);

      if (window.DateManager.differenceInDays(date, new Date()) >= 1) {
        return date;
      }

      return window.DateManager.subtractDay(date);
    },

    types() {
      return this.$store.getters['IncidentClaimTypes/all'];
    },

    claimType() {
      return this.types.find(
        type => type.id == this.form.incident_claim_type_id
      );
    },

    claimRoutes() {
      return this.$store.getters['ClaimRoutes/all'];
    },

    source() {
      return this.$store.getters['Incident/source'];
    },

    handler() {
      return this.$store.getters['Incident/handler'];
    },

    handlers() {
      return this.$store.getters['ClaimHandlers/all'];
    },

    theftStatuses() {
      return this.$store.getters['TheftStatuses/all'];
    },

    author() {
      return this.$store.getters['Incident/author'];
    },

    referrers() {
      return this.$store.getters['Companies/referrers'];
    },

    vulnerabilityCodes() {
      return this.$store.getters['VulnerabilityCodes/all'];
    }
  },

  methods: {
    setActive(type) {
      this.referrer_type = type;
    },

    clearFormCompany() {
      this.form.referrer_id = null;
    },

    clearFormIndividual() {
      this.form.referrer_first_name = null;
      this.form.referrer_last_name = null;
      this.form.referrer_telephone = null;
    },

    mount() {
      this.$store.commit('ClaimRoutes/retrieve');
      this.$store.commit('ClaimHandlers/retrieve');
      this.$store.commit('Companies/retrieve');
      this.$store.commit('TheftStatuses/retrieve');
      this.$store.commit('VulnerabilityCodes/retrieve');

      if (this.source) {
        this.setSourceDetails(this.source);
      }

      if (!this.form.referred_at) {
        this.form.referred_at = new Date();
      }
    },

    setSourceDetails(state) {
      this.source_exists = true;
      this.form.claim_reference = state.claim_reference;
      this.form.source_reference = state.source_reference;
      this.form.incident_occurred_at = state.incident_occurred_at;
      this.form.referred_at = state.referred_at;
      this.form.referrer_id = state.referrer_id;
      this.form.referrer_first_name = state.referrer_first_name;
      this.form.referrer_last_name = state.referrer_last_name;
      this.form.referrer_telephone = state.referrer_telephone;
      this.form.referrer_email_address = state.referrer_email_address;
      this.form.individual = state.individual;
      this.form.incident_claim_type_id = state.incident_claim_type_id;
      this.form.claim_route_id = state.claim_route_id;
      this.form.theft_status_id = state.theft_status_id;
      this.form.vulnerable = state.vulnerable;
      this.form.vulnerability_code_id = state.vulnerability_code_id;
      this.form.information_provided = state.information_provided;
      this.form.support_required = state.support_required;
      this.form.support_provided = state.support_provided;
      this.form.solicitors_reference = state.solicitors_reference;

      this.solicitorForm.company_id = state.solicitor_id;
      this.solicitorForm.name = state.solicitor_name;
      this.solicitorForm.email_address = state.solicitor_email_address;
      this.solicitorForm.telephone_number = state.solicitor_telephone_number;
      this.solicitorForm.address_line_one = state.solicitor_address_line_one;
      this.solicitorForm.address_line_two = state.solicitor_address_line_two;
      this.solicitorForm.city = state.solicitor_city;
      this.solicitorForm.postcode = state.solicitor_postcode;

      if (state.referrer_id) {
        this.referrer_type = 'company';
      } else {
        if (
          state.referrer_first_name ||
          state.referrer_last_name ||
          state.referrer_telephone
        ) {
          this.referrer_type = 'individual';
        } else {
          this.referrer_type = 'none';
        }
      }

      this.companyLoaded = true;

      this.$emit('exists');
    },

    /**
     * If the user tries to change tab when there are unsaved changes in the form we
     * want to display a popup warning them they have unsaved changes.
     *
     * To do that we add a form @change event listener to the form and then emit an
     * 'unsaved-data' event whenever a form value changes. This is then picked up
     * in the parent component and that handles displaying the warning if the user
     * tries to change tabs.
     *
     * This is not to do with the "Assign Handler" functionality.
     */
    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }
      this.$emit('unsaved-data', this.data_entered);
    },

    submit() {
      if (this.readonly) return;
      this.setSolicitor(this.$refs.companySelector.form);

      if (this.source_exists) {
        this.makeRequest('put');
        // reset monitoring of form changes
        this.data_entered = false;
        this.$emit('unsaved-data', this.data_entered);
        return;
      }

      this.makeRequest('post');
      // reset monitoring of form changes
      this.data_entered = false;
      this.$emit('unsaved-data', this.data_entered);
    },

    makeRequest(method) {
      this.form[method]('/incidents/' + this.incident + '/source')
        .then(response => {
          if (!this.source_exists) {
            this.source_exists = true;
            this.$emit('created');
            window.toastify.success(
              'Success!',
              'You can now access the other tabs'
            );
          } else {
            window.toastify.success(
              'Saved',
              'The source was saved successfully!'
            );
          }

          this.$store.commit('Incident/setSource', response);

          this.form.reset();
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Sorry, there was an error creating the source'
          );
        });
    },

    setIncidentDate(value) {
      this.form.incident_occurred_at = value;
    },

    setReferredDate(value) {
      this.form.referred_at = value;
    },

    openAssignHandlerModal() {
      if (this.readonly || !this.hasPermission('claims.reassign')) return;
      this.modals.assign = true;
    },

    handlerWasAssigned(response) {
      this.$store.commit('Incident/setHandler', response.assigned);
      this.modals.assign = false;
    },

    closeAssignToHandlerModal() {
      this.modals.assign = false;
    },

    cancel() {
      if (this.readonly) return;
      window.Ajax.delete('/incidents/' + this.incident)
        .then(response => {
          this.$router.push('/first-notice-of-loss');
        })
        .catch(response => {});
    },

    setSolicitor(company) {
      this.form.solicitor_id = company.company_id;
      this.form.solicitor_name = company.name;
      this.form.solicitor_email_address = company.email_address;
      this.form.solicitor_telephone_number = company.telephone_number;
      this.form.solicitor_address_line_one = company.address_line_one;
      this.form.solicitor_address_line_two = company.address_line_two;
      this.form.solicitor_city = company.city;
      this.form.solicitor_postcode = company.postcode;
    }
  },

  watch: {
    source(source) {
      if (source) {
        this.setSourceDetails(source);
      }
    }
  }
};
</script>
